/** @jsx jsx */
import { jsx } from "theme-ui"
import PropTypes from "prop-types"
import { useEffect } from "react"

import ConsentGate from "./consent-gate"

import { ab } from "../theme"

// Default consent-gate copy for the b-ite embed, keyed by language. The consent
// category is DEV-4 "zu klären" — b-ite loads a third-party script, so we fail
// closed to the strictest category until GBA-Datenschutz confirms it.
const DEFAULT_CONSENT_CATEGORY = "marketing"
const CONSENT_COPY = {
  de: {
    infoText:
      "Die Anzeige der offenen Stellen ist deaktiviert, solange die dafür erforderlichen Cookies nicht akzeptiert wurden.",
    buttonText: "Cookie-Einstellungen öffnen",
  },
  en: {
    infoText:
      "The job listings are disabled until you accept the required cookies.",
    buttonText: "Open cookie settings",
  },
}

// Renders the b-ite jobs listing. Isolated so the loader script is only injected
// once this is mounted — i.e. after consent, when ConsentGate renders its
// children. Before consent it never mounts, so no empty gray slot shows.
const BIteEmbed = ({ listingId }) => {
  useEffect(() => {
    const script = document.createElement("script")
    script.src =
      "https://static.b-ite.com/jobs-api/loader-v1/api-loader-v1.min.js"
    script.async = false
    // script.onload = () => this.scriptLoaded();

    document.body.appendChild(script)
  }, [])

  return <div data-bite-jobs-api-listing={listingId}></div>
}

BIteEmbed.propTypes = {
  listingId: PropTypes.string.isRequired,
}

const BIteTool = ({
  currentLanguage,
  headline,
  description,
  consentCategory,
  buttonText,
  infoText,
  infoUrl,
  infoUrlText,
}) => {
  const defaultLang = process.env.GATSBY_DEFAULT_LANG
    ? process.env.GATSBY_DEFAULT_LANG
    : "de"

  const listingId =
    currentLanguage === defaultLang ? "gba:main-listing-v5" : "gba:english-v5"

  const copy = currentLanguage === "de" ? CONSENT_COPY.de : CONSENT_COPY.en

  return (
    <div
      sx={{
        variant: "innerSpacingContainer",
        pt: "0 !important",
        maxWidth: "1100px",
        "& .bite-container .bite-four-columns .bite-block .bite-headline": {
          color: "text",
          fontWeight: "normal",
        },
        "& .bite-container .bite-four-columns .bite-block input, .bite-container .bite-four-columns .bite-block select":
          {
            variant: "form.formField",
            p: "15px",
            border: "none",
          },
        "& .bite-container .bite-four-columns .bite-block button": {
          backgroundColor: "background",
          color: "text",
          borderColor: "text",
          borderRadius: "20px",
          fontSize: 2,
          fontWeight: "normal",
          p: "10px",
          my: ab({ md: "50px" }),
          cursor: "pointer",
          textTransform: "lowercase",
          "&::first-letter": {
            textTransform: "uppercase",
          },

          "&:hover": {
            color: "background",
            backgroundColor: "text",
          },

          "&.bite-button-reset": {
            border: "none",
            textDecoration: "underline",
            "&:hover": {
              color: "text",
              backgroundColor: "background",
            },
          },
        },
        "& .bite-container .bite-jobs-list-table ": {
          fontSize: 2,
          "& th": {
            backgroundColor: "background",
            color: "text",
            border: "none",
            pl: 0,
          },
          "& td": {
            p: "15px",
            pl: 0,
            borderTop: "none",
            borderRight: "none",
            borderLeft: "none",
            "&.bite-jobs-list--custom_custom_field5": {
              color: "text",
            },
          },
        },
      }}
    >
      {headline && (
        <h2
          sx={{
            variant: "styles.h2",
          }}
        >
          {headline}
        </h2>
      )}
      {description && <div>{description}</div>}
      <ConsentGate
        category={consentCategory}
        buttonText={buttonText || copy.buttonText}
        infoText={infoText || copy.infoText}
        infoUrl={infoUrl}
        infoUrlText={infoUrlText}
      >
        <BIteEmbed listingId={listingId} />
      </ConsentGate>
    </div>
  )
}

BIteTool.propTypes = {
  headline: PropTypes.string,
  description: PropTypes.node,
  consentCategory: PropTypes.string,
  buttonText: PropTypes.string,
  infoText: PropTypes.string,
  infoUrl: PropTypes.string,
  infoUrlText: PropTypes.string,
}

BIteTool.defaultProps = {
  currentLanguage: "de",
  consentCategory: DEFAULT_CONSENT_CATEGORY,
}

export default BIteTool
